import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { HiOutlineTrash, HiPlus } from 'react-icons/hi';

import { getChannel } from '../../api/stores';
import { getChannelCalendarStatus, getGoogleCalendarStatus, getGoogleCalendars, createChannelCalendars, updateChannelCalendars } from '../../api/calendar';
import { getReservations } from '../../api/reservations';
import { getSystemStatus } from '../../api/system';
import ReservationModal from '../../components/reservations/ReservationModal';


const DAY_LABELS = ['일', '월', '화', '수', '목', '금', '토'];

const DAYS = ['월', '화', '수', '목', '금', '토', '일'];
const WEEKDAYS = ['월', '화', '수', '목', '금'];
const WEEKEND = ['토', '일'];
const DEFAULT_SLOT = { start: '09:00', end: '18:00' };

const makeDefaultHours = () =>
    Object.fromEntries(DAYS.map((d) => [d, { enabled: false, slots: [] }]));

const timeToMins = (t) => {
    const [h, m] = t.split(':').map(Number);
    return h * 60 + m;
};

const minsToTime = (mins) => {
    const h = Math.floor(mins / 60) % 24;
    const m = mins % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

const isSlotInvalid = (slots, idx) => {
    const s = slots[idx];
    const start = timeToMins(s.start);
    const end = timeToMins(s.end);
    if (start >= end) return true;
    return slots.some((other, i) => {
        if (i === idx) return false;
        return start < timeToMins(other.end) && end > timeToMins(other.start);
    });
};
const SOURCE_COLORS = ['#34C759', '#F5A623', '#AF52DE', '#FF6B6B', '#00C7BE'];

const formatTime = (dateTimeStr) => {
    if (!dateTimeStr) return '';
    const timePart = dateTimeStr.split('T')[1];
    if (!timePart) return '';
    const [h, m] = timePart.split(':');
    return `${h}:${m}`;
};

const buildCalendarGrid = (year, month) => {
    const firstDay = new Date(year, month, 1).getDay();
    const lastDate = new Date(year, month + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < firstDay; i++) cells.push(null);
    for (let d = 1; d <= lastDate; d++) cells.push(d);
    return cells;
};

const buildCalendarColorMap = (events) => {
    const map = new Map();
    let sourceIdx = 0;
    events.forEach((ev) => {
        if (!map.has(ev.calendar_id)) {
            const color = ev.role === 'TARGET'
                ? '#487BFD'
                : SOURCE_COLORS[sourceIdx++ % SOURCE_COLORS.length];
            map.set(ev.calendar_id, { name: ev.calendar_name, color, role: ev.role });
        }
    });
    return map;
};


const ChannelDetailPage = () => {
    const { uuid, channelUuid } = useParams();
    const navigate = useNavigate();
    const [채널, set채널] = useState(null);
    const [로딩중, set로딩중] = useState(true);
    const [에러, set에러] = useState(null);
    const [캘린더상태, set캘린더상태] = useState(null);
const [캘린더설정모달열림, set캘린더설정모달열림] = useState(false);
    const [캘린더설정단계, set캘린더설정단계] = useState(1);
    const [구글캘린더목록, set구글캘린더목록] = useState([]);
    const [캘린더목록로딩중, set캘린더목록로딩중] = useState(false);
    const [선택된타겟, set선택된타겟] = useState(null);
    const [선택된소스들, set선택된소스들] = useState([]);
    const [캘린더설정저장중, set캘린더설정저장중] = useState(false);
    const [예약목록, set예약목록] = useState([]);
    const [예약모달열림, set예약모달열림] = useState(false);
    const [날짜모달, set날짜모달] = useState(null);
    const [운영시간, set운영시간] = useState(makeDefaultHours());
    const [평일슬롯, set평일슬롯] = useState([{ ...DEFAULT_SLOT }]);

    const [오늘, set오늘] = useState(null);
    const [뷰년, set뷰년] = useState(new Date().getFullYear());
    const [뷰월, set뷰월] = useState(new Date().getMonth());

    const calendarCells = buildCalendarGrid(뷰년, 뷰월);
    const 캘린더색상맵 = buildCalendarColorMap(예약목록);

    const 날짜별예약 = {};
    예약목록.forEach((ev) => {
        const [y, m, d] = ev.start.dateTime.split('T')[0].split('-').map(Number);
        if (y === 뷰년 && m - 1 === 뷰월) {
            if (!날짜별예약[d]) 날짜별예약[d] = [];
            날짜별예약[d].push(ev);
        }
    });
    Object.values(날짜별예약).forEach((list) => {
        list.sort((a, b) => a.start.dateTime.localeCompare(b.start.dateTime));
    });

    const prevMonth = () => {
        if (뷰월 === 0) { set뷰년(y => y - 1); set뷰월(11); }
        else set뷰월(m => m - 1);
    };
    const nextMonth = () => {
        if (뷰월 === 11) { set뷰년(y => y + 1); set뷰월(0); }
        else set뷰월(m => m + 1);
    };

    const reload예약 = () => {
        const startDate = `${뷰년}-${String(뷰월 + 1).padStart(2, '0')}-01`;
        const lastDay = new Date(뷰년, 뷰월 + 1, 0).getDate();
        const endDate = `${뷰년}-${String(뷰월 + 1).padStart(2, '0')}-${lastDay}`;
        getReservations(uuid, channelUuid, startDate, endDate)
            .then((data) => { set예약목록(Array.isArray(data?.events) ? data.events : []); })
            .catch(() => {});
    };

    const open캘린더설정모달 = () => {
        getGoogleCalendarStatus()
            .then((data) => {
                if (!data?.connected) {
                    const confirmed = window.confirm('구글 캘린더와 연동이 필요해요!\n확인을 누르면 구글 캘린더 연동을 시작합니다.');
                    if (!confirmed) return;
                    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
                    const redirectUri = `${window.location.origin}/accounts/me/calendar/google/callback`;
                    const scope = 'https://www.googleapis.com/auth/calendar https://www.googleapis.com/auth/calendar.events';
                    sessionStorage.setItem('calendarConnectReturnTo', window.location.pathname);
                    window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${clientId}&redirect_uri=${encodeURIComponent(redirectUri)}&response_type=code&scope=${encodeURIComponent(scope)}&access_type=offline&prompt=consent`;
                    return;
                }
                set선택된타겟(캘린더상태?.target?.external_id ?? null);
                set선택된소스들((캘린더상태?.sources ?? []).map((s) => s.external_id));
                set캘린더설정단계(1);
                set구글캘린더목록([]);
                set캘린더목록로딩중(true);
                set캘린더설정모달열림(true);
                getGoogleCalendars()
                    .then((list) => {
                        const 배열 = Array.isArray(list) ? list : Array.isArray(list?.calendars) ? list.calendars : [];
                        set구글캘린더목록(배열);
                    })
                    .catch(() => {})
                    .finally(() => { set캘린더목록로딩중(false); });
            })
            .catch(() => {
                alert('캘린더 연동 상태를 확인하지 못했습니다.');
            });
    };

    const save캘린더설정 = () => {
        const targetCal = 구글캘린더목록.find((c) => c.id === 선택된타겟);
        const sourceCals = 구글캘린더목록.filter((c) => 선택된소스들.includes(c.id));
        const body = {
            target: { external_id: targetCal.id, calendar_name: targetCal.title },
            sources: sourceCals.map((c) => ({ external_id: c.id, calendar_name: c.title })),
        };
        set캘린더설정저장중(true);
        const 저장요청 = 캘린더상태?.target
            ? updateChannelCalendars(uuid, channelUuid, body)
            : createChannelCalendars(uuid, channelUuid, body).catch((err) => {
                const status = err?.response?.status;
                if (status === 409 || status === 500) {
                    return updateChannelCalendars(uuid, channelUuid, body);
                }
                throw err;
            });
        저장요청
            .then(() => getChannelCalendarStatus(uuid, channelUuid))
            .then((데이터) => {
                set캘린더상태(데이터);
                set캘린더설정모달열림(false);
            })
            .catch(() => { alert('설정 저장 중 오류가 발생했습니다.'); })
            .finally(() => { set캘린더설정저장중(false); });
    };

    const applyTodays = (days, slots) => {
        set운영시간((prev) => {
            const next = { ...prev };
            days.forEach((d) => {
                next[d] = { enabled: slots.length > 0, slots: slots.map((s) => ({ ...s })) };
            });
            return next;
        });
    };
    const applyPreset = (days) => applyTodays(days, [{ ...DEFAULT_SLOT }]);
    const resetAll = () => set운영시간(makeDefaultHours());
    const toggleDay = (day) => {
        set운영시간((prev) => {
            const { enabled, slots } = prev[day];
            const newEnabled = !enabled;
            return { ...prev, [day]: { enabled: newEnabled, slots: newEnabled && slots.length === 0 ? [{ ...DEFAULT_SLOT }] : slots } };
        });
    };
    const addSlot = (day) => {
        set운영시간((prev) => {
            const slots = prev[day].slots;
            let newStart = '09:00';
            let newEnd = '10:00';
            if (slots.length > 0) {
                const maxEnd = Math.max(...slots.map((s) => timeToMins(s.end)));
                newStart = minsToTime(maxEnd);
                newEnd = minsToTime(Math.min(maxEnd + 60, 23 * 60 + 59));
            }
            return { ...prev, [day]: { ...prev[day], slots: [...slots, { start: newStart, end: newEnd }] } };
        });
    };
    const removeSlot = (day, idx) => {
        set운영시간((prev) => {
            const slots = prev[day].slots.filter((_, i) => i !== idx);
            return { ...prev, [day]: { enabled: slots.length > 0, slots } };
        });
    };
    const updateSlot = (day, idx, field, val) => {
        set운영시간((prev) => {
            const slots = prev[day].slots.map((s, i) => i === idx ? { ...s, [field]: val } : s);
            return { ...prev, [day]: { ...prev[day], slots } };
        });
    };
    const addBulkSlot = () => {
        set평일슬롯((prev) => {
            const maxEnd = prev.length > 0 ? Math.max(...prev.map((s) => timeToMins(s.end))) : timeToMins('18:00');
            const newStart = minsToTime(maxEnd);
            const newEnd = minsToTime(Math.min(maxEnd + 60, 23 * 60 + 59));
            return [...prev, { start: newStart, end: newEnd }];
        });
    };
    const removeBulkSlot = (idx) => {
        set평일슬롯((prev) => prev.filter((_, i) => i !== idx));
    };
    const updateBulkSlot = (idx, field, val) => {
        set평일슬롯((prev) => prev.map((s, i) => i === idx ? { ...s, [field]: val } : s));
    };

    useEffect(() => {
        getSystemStatus()
            .then((data) => {
                // "2026-04-26 09:14:40" 형태
                const [datePart] = data.current_time.split(' ');
                const [y, m, d] = datePart.split('-').map(Number);
                set오늘({ year: y, month: m - 1, date: d });
                set뷰년(y);
                set뷰월(m - 1);
            })
            .catch(() => {
                const now = new Date();
                set오늘({ year: now.getFullYear(), month: now.getMonth(), date: now.getDate() });
            });
    }, []);

    useEffect(() => {
        getChannel(uuid, channelUuid)
            .then((채널데이터) => { set채널(채널데이터); })
            .catch(() => { set에러('정보를 불러오지 못했습니다.'); })
            .finally(() => { set로딩중(false); });
    }, [uuid, channelUuid]);

    useEffect(() => {
        getChannelCalendarStatus(uuid, channelUuid)
            .then((데이터) => {
                set캘린더상태(데이터);
            })
            .catch(() => {});
    }, [uuid, channelUuid]);

    useEffect(() => {
        const startDate = `${뷰년}-${String(뷰월 + 1).padStart(2, '0')}-01`;
        const lastDay = new Date(뷰년, 뷰월 + 1, 0).getDate();
        const endDate = `${뷰년}-${String(뷰월 + 1).padStart(2, '0')}-${lastDay}`;
        getReservations(uuid, channelUuid, startDate, endDate)
            .then((data) => {
                set예약목록(Array.isArray(data?.events) ? data.events : []);
            })
            .catch(() => {});
    }, [uuid, channelUuid, 뷰년, 뷰월]);

    return (
        <PageWrapper>
            <Contents>
                <BackButton onClick={() => navigate(`/stores/${uuid}/channels`)}>
                    ← 채널 목록
                </BackButton>

                {로딩중 && <StatusText>불러오는 중...</StatusText>}
                {!로딩중 && 에러 && <StatusText $error>{에러}</StatusText>}

                {!로딩중 && !에러 && 채널 && (
                    <>
                        <PageTitleRow>
                            <PageTitle>{채널.name ?? '채널'}</PageTitle>
                        </PageTitleRow>

                        <InfoCard>
                            <InfoRow>
                                <InfoLabel>소개</InfoLabel>
                                <InfoValue>{채널.bio ?? '-'}</InfoValue>
                            </InfoRow>
                            <InfoRow>
                                <InfoLabel>상태</InfoLabel>
                                <InfoValue>{채널.status ?? '-'}</InfoValue>
                            </InfoRow>
                            <InfoRow>
                                <InfoLabel>생성일</InfoLabel>
                                <InfoValue>
                                    {채널.created_at
                                        ? new Date(채널.created_at).toLocaleString('ko-KR')
                                        : '-'}
                                </InfoValue>
                            </InfoRow>
                        </InfoCard>

                        <CalendarCard>
                            <CalendarCardHeader>
                                <CalendarCardTitle>연동된 캘린더</CalendarCardTitle>
                                <OnboardingButton onClick={open캘린더설정모달}>
                                    설정 변경
                                </OnboardingButton>
                            </CalendarCardHeader>

                            {캘린더상태 ? (
                                <CalendarSection>
                                    <CalendarSectionTitle>쓰기 전용</CalendarSectionTitle>
                                    {캘린더상태.target ? (
                                        <CalendarItem>
                                            <CalendarDot />
                                            <CalendarItemName>{캘린더상태.target.calendar_name}</CalendarItemName>
                                            <CalendarBadge>예약 저장</CalendarBadge>
                                        </CalendarItem>
                                    ) : (
                                        <EmptyText>설정된 캘린더가 없습니다.</EmptyText>
                                    )}
                                    <CalendarSectionTitle style={{ marginTop: '16px' }}>읽기 전용</CalendarSectionTitle>
                                    {캘린더상태.sources?.length > 0 ? (
                                        캘린더상태.sources.map((캘린더) => (
                                            <CalendarItem key={캘린더.id}>
                                                <CalendarDot $dim />
                                                <CalendarItemName>{캘린더.calendar_name}</CalendarItemName>
                                                <CalendarBadge $dim>일정 읽기</CalendarBadge>
                                            </CalendarItem>
                                        ))
                                    ) : (
                                        <EmptyText>설정된 캘린더가 없습니다.</EmptyText>
                                    )}
                                </CalendarSection>
                            ) : (
                                <EmptyText>캘린더가 연동되어 있지 않습니다.</EmptyText>
                            )}
                        </CalendarCard>

                        <AvailableHoursCard>
                            <AvailableHoursHeader>
                                <AvailableHoursTitle>예약 가능 시간</AvailableHoursTitle>
                            </AvailableHoursHeader>

                            <PresetBar>
                                <PresetLabel>빠른 설정</PresetLabel>
                                <PresetButton onClick={() => applyPreset(WEEKDAYS)}>평일</PresetButton>
                                <PresetButton onClick={() => applyPreset(WEEKEND)}>주말</PresetButton>
                                <PresetButton onClick={() => applyPreset(DAYS)}>매일</PresetButton>
                                <ResetButton onClick={resetAll}>초기화</ResetButton>
                            </PresetBar>

                            <BulkCard>
                                <BulkHeader>
                                    <BulkTitle>평일 (월~금) 일괄 설정</BulkTitle>
                                </BulkHeader>
                                <BulkBody>
                                    <BulkSlots>
                                        {평일슬롯.map((slot, idx) => {
                                            const invalid = isSlotInvalid(평일슬롯, idx);
                                            return (
                                                <SlotRow key={idx}>
                                                    <TimeInput
                                                        type="time"
                                                        value={slot.start}
                                                        $invalid={invalid}
                                                        onChange={(e) => updateBulkSlot(idx, 'start', e.target.value)}
                                                    />
                                                    <SlotSep>~</SlotSep>
                                                    <TimeInput
                                                        type="time"
                                                        value={slot.end}
                                                        $invalid={invalid}
                                                        onChange={(e) => updateBulkSlot(idx, 'end', e.target.value)}
                                                    />
                                                    <DeleteSlotButton onClick={() => removeBulkSlot(idx)}>
                                                        <HiOutlineTrash size={16} />
                                                    </DeleteSlotButton>
                                                </SlotRow>
                                            );
                                        })}
                                        {평일슬롯.some((_, idx) => isSlotInvalid(평일슬롯, idx)) && (
                                            <OverlapWarning>중복된 시간대가 있어요</OverlapWarning>
                                        )}
                                        <AddSlotButton onClick={addBulkSlot}>
                                            <HiPlus size={14} />시간 추가
                                        </AddSlotButton>
                                    </BulkSlots>
                                    <ApplyButton
                                        onClick={() => applyTodays(WEEKDAYS, 평일슬롯)}
                                        disabled={평일슬롯.length === 0 || 평일슬롯.some((_, idx) => isSlotInvalid(평일슬롯, idx))}
                                    >
                                        월~금에 적용
                                    </ApplyButton>
                                </BulkBody>
                            </BulkCard>

                            <HoursCard>
                                {DAYS.map((day) => {
                                    const { enabled, slots } = 운영시간[day];
                                    return (
                                        <DayRow key={day}>
                                            <DayBadge $active={enabled} onClick={() => toggleDay(day)}>{day}</DayBadge>
                                            <DayContent>
                                                {!enabled ? (
                                                    <EmptySlotText>가능한 시간 없음</EmptySlotText>
                                                ) : (
                                                    <>
                                                        {slots.map((slot, idx) => {
                                                            const invalid = isSlotInvalid(slots, idx);
                                                            return (
                                                                <SlotRow key={idx}>
                                                                    <TimeInput
                                                                        type="time"
                                                                        value={slot.start}
                                                                        $invalid={invalid}
                                                                        onChange={(e) => updateSlot(day, idx, 'start', e.target.value)}
                                                                    />
                                                                    <SlotSep>~</SlotSep>
                                                                    <TimeInput
                                                                        type="time"
                                                                        value={slot.end}
                                                                        $invalid={invalid}
                                                                        onChange={(e) => updateSlot(day, idx, 'end', e.target.value)}
                                                                    />
                                                                    <DeleteSlotButton onClick={() => removeSlot(day, idx)}>
                                                                        <HiOutlineTrash size={16} />
                                                                    </DeleteSlotButton>
                                                                </SlotRow>
                                                            );
                                                        })}
                                                        {slots.some((_, idx) => isSlotInvalid(slots, idx)) && (
                                                            <OverlapWarning>중복된 시간대가 있어요</OverlapWarning>
                                                        )}
                                                        <AddSlotButton onClick={() => addSlot(day)}>
                                                            <HiPlus size={14} />시간 추가
                                                        </AddSlotButton>
                                                    </>
                                                )}
                                            </DayContent>
                                        </DayRow>
                                    );
                                })}
                            </HoursCard>
                        </AvailableHoursCard>

                        <MonthCalendarCard>
                            <MonthCalendarTitleRow>
                                <MonthCalendarCardTitle>예약</MonthCalendarCardTitle>
                                <AddReservationButton onClick={() => set예약모달열림(true)}>
                                    + 예약 추가
                                </AddReservationButton>
                            </MonthCalendarTitleRow>
                            <MonthCalendarHeader>
                                <NavButton onClick={prevMonth}>‹</NavButton>
                                <MonthLabel>{뷰년}년 {뷰월 + 1}월</MonthLabel>
                                <NavButton onClick={nextMonth}>›</NavButton>
                            </MonthCalendarHeader>

                            <DayGrid>
                                {DAY_LABELS.map((d, i) => (
                                    <DayLabel key={d} $sun={i === 0} $sat={i === 6}>{d}</DayLabel>
                                ))}
                                {calendarCells.map((day, i) => {
                                    const col = i % 7;
                                    const isToday =
                                        day &&
                                        오늘 &&
                                        뷰년 === 오늘.year &&
                                        뷰월 === 오늘.month &&
                                        day === 오늘.date;
                                    const dayEvents = day ? (날짜별예약[day] ?? []) : [];
                                    const shown = dayEvents.slice(0, 3);
                                    const extra = dayEvents.length - shown.length;
                                    return (
                                        <DayCell key={i} $empty={!day}>
                                            <DateNumber
                                                $today={isToday}
                                                $sun={col === 0}
                                                $sat={col === 6}
                                                $empty={!day}
                                            >
                                                {day ?? ''}
                                            </DateNumber>
                                            <EventList>
                                                {shown.map((ev, idx) => {
                                                    const color = 캘린더색상맵.get(ev.calendar_id)?.color ?? '#487BFD';
                                                    const start = formatTime(ev.start?.dateTime);
                                                    const end = formatTime(ev.end?.dateTime);
                                                    const timeLabel = start && end ? `${start}-${end} ` : start ? `${start} ` : '';
                                                    return (
                                                        <EventLabel key={idx} $color={color}>
                                                            <EventLabelTime>{timeLabel}</EventLabelTime>
                                                            {ev.summary ?? ''}
                                                        </EventLabel>
                                                    );
                                                })}
                                                {extra > 0 && (
                                                    <MoreLabel onClick={(e) => { e.stopPropagation(); set날짜모달({ day, events: dayEvents }); }}>
                                                        +{extra}개 더보기
                                                    </MoreLabel>
                                                )}
                                            </EventList>
                                        </DayCell>
                                    );
                                })}
                            </DayGrid>

                            {캘린더색상맵.size > 0 && (
                                <CalendarLegend>
                                    {[...캘린더색상맵.values()].map((cal) => (
                                        <LegendItem key={cal.name}>
                                            <LegendDot $color={cal.color} />
                                            <LegendName>{cal.name}</LegendName>
                                        </LegendItem>
                                    ))}
                                </CalendarLegend>
                            )}
                        </MonthCalendarCard>
                    </>
                )}
            </Contents>

{예약모달열림 && (
                <ReservationModal
                    storeUuid={uuid}
                    channelUuid={channelUuid}
                    onClose={() => set예약모달열림(false)}
                    onSuccess={reload예약}
                />
            )}

{날짜모달 && (
                <ModalOverlay onClick={() => set날짜모달(null)}>
                    <DayEventsBox onClick={(e) => e.stopPropagation()}>
                        <DayEventsHeader>
                            <DayEventsTitle>{뷰년}년 {뷰월 + 1}월 {날짜모달.day}일 예약</DayEventsTitle>
                            <DayEventsCloseBtn onClick={() => set날짜모달(null)}>✕</DayEventsCloseBtn>
                        </DayEventsHeader>
                        <DayEventsList>
                            {날짜모달.events.map((ev, idx) => {
                                const color = 캘린더색상맵.get(ev.calendar_id)?.color ?? '#487BFD';
                                const start = formatTime(ev.start?.dateTime);
                                const end = formatTime(ev.end?.dateTime);
                                return (
                                    <DayEventItem key={idx}>
                                        <DayEventColorBar $color={color} />
                                        <DayEventInfo>
                                            <DayEventTime>{start}{end ? ` ~ ${end}` : ''}</DayEventTime>
                                            <DayEventName>{ev.summary ?? '(제목 없음)'}</DayEventName>
                                        </DayEventInfo>
                                    </DayEventItem>
                                );
                            })}
                        </DayEventsList>
                    </DayEventsBox>
                </ModalOverlay>
            )}

{캘린더설정모달열림 && (
                <ModalOverlay onClick={() => set캘린더설정모달열림(false)}>
                    <ModalBox onClick={(e) => e.stopPropagation()}>
                        <ModalProgressBar>
                            {[1, 2].map((s) => (
                                <ModalProgressStep key={s} $active={s <= 캘린더설정단계} />
                            ))}
                        </ModalProgressBar>

                        <ModalStepLabel>단계 {캘린더설정단계} / 2</ModalStepLabel>

                        {캘린더설정단계 === 1 && (
                            <ModalStepView>
                                <ModalStepTitle>예약을 저장할 캘린더</ModalStepTitle>
                                <ModalStepDesc>
                                    새 예약이 들어오면 선택한 캘린더에 자동으로 등록됩니다.<br />
                                    하나만 선택해 주세요.
                                </ModalStepDesc>
                                {캘린더목록로딩중 ? (
                                    <ModalLoadingText>캘린더 목록 불러오는 중...</ModalLoadingText>
                                ) : (
                                    <ModalCalendarList>
                                        {구글캘린더목록.map((cal) => (
                                            <ModalCalendarItem
                                                key={cal.id}
                                                $selected={선택된타겟 === cal.id}
                                                onClick={() => {
                                                set선택된타겟(cal.id);
                                            }}
                                            >
                                                <ModalCalDot $color={cal.background_color} />
                                                <ModalCalName>{cal.title}</ModalCalName>
                                                <ModalRadioCircle $selected={선택된타겟 === cal.id}>
                                                    {선택된타겟 === cal.id && <ModalRadioDot />}
                                                </ModalRadioCircle>
                                            </ModalCalendarItem>
                                        ))}
                                    </ModalCalendarList>
                                )}
                            </ModalStepView>
                        )}

                        {캘린더설정단계 === 2 && (
                            <ModalStepView>
                                <ModalStepTitle>내 일정을 가져올 캘린더</ModalStepTitle>
                                <ModalStepDesc>
                                    선택한 캘린더의 일정을 확인해 이중 예약을 방지합니다.<br />
                                    여러 개 선택할 수 있습니다.
                                </ModalStepDesc>
                                <ModalCalendarList>
                                    {구글캘린더목록.map((cal) => {
                                        const checked = 선택된소스들.includes(cal.id);
                                        const isTarget = cal.id === 선택된타겟;
                                        return (
                                            <ModalCalendarItem
                                                key={cal.id}
                                                $selected={checked}
                                                $locked={isTarget}
                                                onClick={() => {
                                                    if (isTarget) return;
                                                    set선택된소스들((prev) =>
                                                        checked ? prev.filter((id) => id !== cal.id) : [...prev, cal.id]
                                                    );
                                                }}
                                            >
                                                <ModalCalDot $color={cal.background_color} />
                                                <ModalCalName>{cal.title}</ModalCalName>
                                                <ModalCheckboxBox $selected={checked} $locked={isTarget}>
                                                    {checked && <ModalCheckMark>✓</ModalCheckMark>}
                                                </ModalCheckboxBox>
                                            </ModalCalendarItem>
                                        );
                                    })}
                                </ModalCalendarList>
                            </ModalStepView>
                        )}

                        <ModalActions>
                            {캘린더설정단계 > 1 ? (
                                <ModalCancelButton onClick={() => set캘린더설정단계((p) => p - 1)}>이전</ModalCancelButton>
                            ) : (
                                <ModalCancelButton onClick={() => set캘린더설정모달열림(false)}>취소</ModalCancelButton>
                            )}
                            {캘린더설정단계 < 2 ? (
                                <ModalNextButton
                                    disabled={선택된타겟 === null}
                                    onClick={() => set캘린더설정단계((p) => p + 1)}
                                >
                                    다음
                                </ModalNextButton>
                            ) : (
                                <ModalNextButton
                                    disabled={캘린더설정저장중}
                                    onClick={save캘린더설정}
                                >
                                    {캘린더설정저장중 ? '저장 중...' : '완료'}
                                </ModalNextButton>
                            )}
                        </ModalActions>
                    </ModalBox>
                </ModalOverlay>
            )}
        </PageWrapper>
    );
};


const PageWrapper = styled.div`
    width: 100%;
    min-height: 100dvh;
    background: #f7f8fa;
`;

const Contents = styled.div`
    max-width: 960px;
    margin: 0 auto;
    padding: 80px 16px 48px;
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const BackButton = styled.button`
    background: none;
    border: none;
    padding: 0;
    margin-bottom: 8px;
    font-family: Pretendard;
    font-size: 14px;
    font-weight: 500;
    color: #999999;
    cursor: pointer;
    align-self: flex-start;

    &:hover { color: #111111; }
`;

const PageTitleRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
`;

const PageTitle = styled.h1`
    font-family: Pretendard;
    font-size: 24px;
    font-weight: 700;
    color: #111111;
    margin: 0;
`;


const InfoCard = styled.div`
    background: #ffffff;
    border: 1px solid #eeeeee;
    border-radius: 12px;
    padding: 24px;
    display: flex;
    flex-direction: column;
    gap: 20px;
`;

const InfoRow = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

const InfoLabel = styled.span`
    font-family: Pretendard;
    font-size: 12px;
    font-weight: 500;
    color: #aaaaaa;
    text-transform: uppercase;
    letter-spacing: 0.05em;
`;

const InfoValue = styled.span`
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 400;
    color: #111111;
`;

const CalendarCard = styled.div`
    background: #ffffff;
    border: 1px solid #eeeeee;
    border-radius: 12px;
    padding: 24px;
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const CalendarCardHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
`;

const CalendarCardTitle = styled.span`
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 600;
    color: #111111;
`;

const OnboardingButton = styled.button`
    background: none;
    border: 1px solid #eeeeee;
    border-radius: 6px;
    padding: 5px 12px;
    font-family: Pretendard;
    font-size: 12px;
    font-weight: 500;
    color: #777777;
    cursor: pointer;
    transition: border-color 0.15s, color 0.15s;

    &:hover { border-color: #bbbbbb; color: #333333; }
`;

const CalendarSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const CalendarSectionTitle = styled.span`
    font-family: Pretendard;
    font-size: 11px;
    font-weight: 600;
    color: #aaaaaa;
    text-transform: uppercase;
    letter-spacing: 0.08em;
`;

const CalendarItem = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 14px;
    background: #f7f8fa;
    border: 1px solid #eeeeee;
    border-radius: 8px;
`;

const CalendarDot = styled.div`
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${({ $dim }) => ($dim ? '#bbbbbb' : '#487BFD')};
    flex-shrink: 0;
`;

const CalendarItemName = styled.span`
    flex: 1;
    font-family: Pretendard;
    font-size: 14px;
    font-weight: 500;
    color: #111111;
`;

const CalendarBadge = styled.span`
    font-family: Pretendard;
    font-size: 11px;
    font-weight: 500;
    color: ${({ $dim }) => ($dim ? '#aaaaaa' : '#487BFD')};
    background: ${({ $dim }) => ($dim ? '#f5f5f5' : '#eef2ff')};
    border: 1px solid ${({ $dim }) => ($dim ? '#eeeeee' : '#487BFD44')};
    border-radius: 4px;
    padding: 2px 8px;
`;

const EmptyText = styled.span`
    font-family: Pretendard;
    font-size: 13px;
    font-weight: 400;
    color: #aaaaaa;
`;

/* ── 월간 달력 ── */

const MonthCalendarTitleRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 4px;
`;

const MonthCalendarCardTitle = styled.span`
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 600;
    color: #111111;
`;

const AddReservationButton = styled.button`
    background: #487BFD;
    border: none;
    border-radius: 7px;
    padding: 7px 14px;
    font-family: Pretendard;
    font-size: 13px;
    font-weight: 600;
    color: #ffffff;
    cursor: pointer;
    transition: opacity 0.15s;

    &:hover { opacity: 0.85; }
`;

const MonthCalendarCard = styled.div`
    background: #ffffff;
    border: 1px solid #eeeeee;
    border-radius: 12px;
    padding: 24px;
    display: flex;
    flex-direction: column;
    gap: 0;
`;

const MonthCalendarHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 20px;
`;

const MonthLabel = styled.span`
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 600;
    color: #111111;
`;

const NavButton = styled.button`
    background: none;
    border: none;
    font-size: 20px;
    color: #aaaaaa;
    cursor: pointer;
    padding: 0 8px;
    line-height: 1;

    &:hover { color: #333333; }
`;

const DayGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 1px;
    border-top: 1px solid #eeeeee;
`;

const DayLabel = styled.div`
    font-family: Pretendard;
    font-size: 11px;
    font-weight: 600;
    color: ${({ $sun, $sat }) => $sun ? '#EF4444' : $sat ? '#487BFD' : '#aaaaaa'};
    text-align: center;
    padding: 6px 0 10px;
`;

const DayCell = styled.div`
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 2px;
    padding: 4px 4px 6px;
    min-height: 80px;
    cursor: ${({ $empty }) => ($empty ? 'default' : 'pointer')};
    vertical-align: top;
`;

const DateNumber = styled.span`
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    font-family: Pretendard;
    font-size: 13px;
    font-weight: ${({ $today }) => ($today ? '700' : '400')};
    color: ${({ $empty, $today, $sun, $sat }) => {
        if ($empty) return 'transparent';
        if ($today) return '#ffffff';
        if ($sun) return '#EF444488';
        if ($sat) return '#487BFD88';
        return '#555555';
    }};
    background: ${({ $today }) => ($today ? '#487BFD' : 'transparent')};
    transition: background 0.12s;
    align-self: flex-start;
    margin-bottom: 2px;
`;

const EventList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
    width: 100%;
`;

const EventLabel = styled.div`
    width: 100%;
    border-left: 2px solid ${({ $color }) => $color ?? '#487BFD'};
    background: ${({ $color }) => $color ? `${$color}18` : '#487BFD18'};
    border-radius: 0 3px 3px 0;
    padding: 2px 4px;
    font-family: Pretendard;
    font-size: 10px;
    font-weight: 400;
    color: #444444;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    line-height: 1.4;
    box-sizing: border-box;
`;

const EventLabelTime = styled.span`
    font-weight: 600;
    color: #333333;
`;

const MoreLabel = styled.div`
    font-family: Pretendard;
    font-size: 10px;
    font-weight: 500;
    color: #487BFD;
    padding: 1px 4px;
    cursor: pointer;
    border-radius: 3px;
    transition: background 0.12s;

    &:hover { background: #eef2ff; }
`;

/* ── 날짜 예약 목록 모달 ── */

const DayEventsBox = styled.div`
    background: #ffffff;
    border-radius: 16px;
    padding: 28px 24px 24px;
    width: 100%;
    max-width: 360px;
    max-height: 70vh;
    display: flex;
    flex-direction: column;
    gap: 0;
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.14);
    overflow: hidden;
`;

const DayEventsHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
`;

const DayEventsTitle = styled.span`
    font-family: Pretendard;
    font-size: 16px;
    font-weight: 700;
    color: #111111;
`;

const DayEventsCloseBtn = styled.button`
    background: none;
    border: none;
    padding: 4px 6px;
    font-size: 16px;
    color: #aaaaaa;
    cursor: pointer;
    line-height: 1;
    border-radius: 6px;
    transition: background 0.12s, color 0.12s;

    &:hover { background: #f5f5f5; color: #333333; }
`;

const DayEventsList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    overflow-y: auto;
`;

const DayEventItem = styled.div`
    display: flex;
    align-items: stretch;
    gap: 12px;
    padding: 12px 14px;
    background: #f7f8fa;
    border: 1px solid #eeeeee;
    border-radius: 8px;
`;

const DayEventColorBar = styled.div`
    width: 3px;
    border-radius: 2px;
    background: ${({ $color }) => $color ?? '#487BFD'};
    flex-shrink: 0;
    align-self: stretch;
`;

const DayEventInfo = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const DayEventTime = styled.span`
    font-family: Pretendard;
    font-size: 12px;
    font-weight: 600;
    color: #555555;
`;

const DayEventName = styled.span`
    font-family: Pretendard;
    font-size: 14px;
    font-weight: 500;
    color: #111111;
`;

const CalendarLegend = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    margin-top: 20px;
    padding-top: 16px;
    border-top: 1px solid #eeeeee;
`;

const LegendItem = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
`;

const LegendDot = styled.div`
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${({ $color }) => $color};
    flex-shrink: 0;
`;

const LegendName = styled.span`
    font-family: Pretendard;
    font-size: 12px;
    font-weight: 400;
    color: #777777;
`;

const StatusText = styled.span`
    display: block;
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 400;
    color: ${({ $error }) => ($error ? '#EF4444' : '#aaaaaa')};
`;

const ModalOverlay = styled.div`
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    padding: 24px;
    box-sizing: border-box;
`;

const ModalBox = styled.div`
    background: #ffffff;
    border: 1px solid #eeeeee;
    border-radius: 20px;
    padding: 40px;
    width: 100%;
    max-width: 480px;
    max-height: 80vh;
    display: flex;
    flex-direction: column;
    gap: 28px;
    overflow: hidden;
`;

const ModalProgressBar = styled.div`
    display: flex;
    gap: 6px;
`;

const ModalProgressStep = styled.div`
    flex: 1;
    height: 4px;
    border-radius: 2px;
    background: ${({ $active }) => ($active ? '#487BFD' : '#e0e0e0')};
    transition: background 0.3s;
`;

const ModalStepLabel = styled.span`
    font-family: Pretendard;
    font-size: 12px;
    font-weight: 500;
    color: #aaaaaa;
`;

const ModalStepView = styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;
    overflow-y: auto;
    flex: 1;
    min-height: 0;
`;

const ModalStepTitle = styled.h2`
    font-family: Pretendard;
    font-size: 20px;
    font-weight: 700;
    color: #111111;
    margin: 0;
`;

const ModalStepDesc = styled.p`
    font-family: Pretendard;
    font-size: 14px;
    font-weight: 400;
    color: #777777;
    line-height: 170%;
    margin: 0;
`;

const ModalLoadingText = styled.span`
    font-family: Pretendard;
    font-size: 14px;
    font-weight: 400;
    color: #888888;
    text-align: center;
    padding: 24px 0;
`;

const ModalCalendarList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const ModalCalendarItem = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px 16px;
    border: 1px solid ${({ $selected }) => ($selected ? '#487BFD' : '#eeeeee')};
    border-radius: 10px;
    background: ${({ $selected }) => ($selected ? '#eef2ff' : '#f7f8fa')};
    cursor: ${({ $locked }) => ($locked ? 'default' : 'pointer')};
    transition: border-color 0.15s, background 0.15s;

    &:hover { border-color: ${({ $locked }) => ($locked ? '#487BFD' : '#487BFD44')}; }
`;

const ModalCalDot = styled.div`
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: ${({ $color }) => $color ?? '#cccccc'};
    flex-shrink: 0;
`;

const ModalCalName = styled.span`
    flex: 1;
    font-family: Pretendard;
    font-size: 14px;
    font-weight: 500;
    color: #111111;
`;

const ModalRadioCircle = styled.div`
    width: 18px;
    height: 18px;
    border-radius: 50%;
    border: 2px solid ${({ $selected }) => ($selected ? '#487BFD' : '#cccccc')};
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: border-color 0.15s;
`;

const ModalRadioDot = styled.div`
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #487BFD;
`;

const ModalCheckboxBox = styled.div`
    width: 18px;
    height: 18px;
    border-radius: 4px;
    border: 2px solid ${({ $selected }) => ($selected ? '#487BFD' : '#cccccc')};
    background: ${({ $selected, $locked }) => $locked ? '#487BFD99' : $selected ? '#487BFD' : 'transparent'};
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: border-color 0.15s, background 0.15s;
`;

const ModalCheckMark = styled.span`
    font-size: 11px;
    color: #ffffff;
    font-weight: 700;
    line-height: 1;
`;

const ModalActions = styled.div`
    display: flex;
    gap: 8px;
    justify-content: space-between;
`;

const ModalCancelButton = styled.button`
    background: none;
    border: 1px solid #eeeeee;
    border-radius: 8px;
    padding: 10px 20px;
    font-family: Pretendard;
    font-size: 14px;
    font-weight: 500;
    color: #777777;
    cursor: pointer;
    transition: border-color 0.15s, color 0.15s;

    &:hover { border-color: #bbbbbb; color: #333333; }
`;

const ModalNextButton = styled.button`
    flex: 1;
    background: #487BFD;
    border: none;
    border-radius: 8px;
    padding: 10px 20px;
    font-family: Pretendard;
    font-size: 14px;
    font-weight: 600;
    color: #ffffff;
    cursor: pointer;
    transition: opacity 0.15s;

    &:hover:not(:disabled) { opacity: 0.85; }
    &:disabled { opacity: 0.35; cursor: not-allowed; }
`;


/* ── 예약 가능 시간 ── */

const AvailableHoursCard = styled.div`
    background: #ffffff;
    border: 1px solid #eeeeee;
    border-radius: 12px;
    padding: 24px;
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const AvailableHoursHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
`;

const AvailableHoursTitle = styled.span`
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 600;
    color: #111111;
`;

const PresetBar = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
`;

const PresetLabel = styled.span`
    font-family: Pretendard;
    font-size: 12px;
    font-weight: 500;
    color: #aaaaaa;
    margin-right: 4px;
`;

const PresetButton = styled.button`
    padding: 5px 12px;
    border: 1px solid #e0e0e0;
    border-radius: 6px;
    background: #ffffff;
    font-family: Pretendard;
    font-size: 13px;
    font-weight: 500;
    color: #333333;
    cursor: pointer;
    transition: border-color 0.12s, background 0.12s, color 0.12s;

    &:hover { border-color: #487BFD; background: #eef2ff; color: #487BFD; }
`;

const ResetButton = styled(PresetButton)`
    color: #aaaaaa;
    margin-left: auto;

    &:hover { border-color: #EF4444; background: #fff5f5; color: #EF4444; }
`;

const BulkCard = styled.div`
    border: 1px solid #e0e0e0;
    border-radius: 10px;
    overflow: hidden;
`;

const BulkHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
    background: #f7f8fa;
    border-bottom: 1px solid #e0e0e0;
`;

const BulkTitle = styled.span`
    font-family: Pretendard;
    font-size: 13px;
    font-weight: 600;
    color: #333333;
`;

const BulkBody = styled.div`
    padding: 14px 16px;
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const BulkSlots = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const HoursCard = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0;
    border: 1px solid #e0e0e0;
    border-radius: 10px;
    overflow: hidden;
`;

const DayRow = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 12px 16px;
    border-bottom: 1px solid #f0f0f0;

    &:last-child { border-bottom: none; }
`;

const DayBadge = styled.button`
    width: 36px;
    height: 36px;
    border-radius: 50%;
    border: none;
    background: ${({ $active }) => $active ? '#1e7a5a' : '#f0f0f0'};
    color: ${({ $active }) => $active ? '#ffffff' : '#888888'};
    font-family: Pretendard;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    flex-shrink: 0;
    transition: background 0.15s, color 0.15s;

    &:hover { opacity: 0.85; }
`;

const DayContent = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding-top: 6px;
`;

const SlotRow = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
`;

const TimeInput = styled.input`
    border: 1px solid ${({ $invalid }) => $invalid ? '#ff4444' : '#e0e0e0'};
    background: ${({ $invalid }) => $invalid ? '#fff5f5' : '#fafafa'};
    border-radius: 6px;
    padding: 6px 8px;
    font-family: Pretendard;
    font-size: 14px;
    font-weight: 400;
    color: #111111;
    width: 140px;
    box-sizing: border-box;
    outline: none;
    transition: border-color 0.12s;

    &:focus { border-color: ${({ $invalid }) => $invalid ? '#ff4444' : '#487BFD'}; }
`;

const SlotSep = styled.span`
    font-family: Pretendard;
    font-size: 13px;
    color: #aaaaaa;
`;

const DeleteSlotButton = styled.button`
    background: none;
    border: none;
    padding: 4px;
    color: #cccccc;
    cursor: pointer;
    display: flex;
    align-items: center;
    transition: color 0.12s;

    &:hover { color: #EF4444; }
`;

const AddSlotButton = styled.button`
    display: flex;
    align-items: center;
    gap: 4px;
    background: none;
    border: 1px dashed #d0d0d0;
    border-radius: 6px;
    padding: 5px 10px;
    font-family: Pretendard;
    font-size: 12px;
    font-weight: 500;
    color: #aaaaaa;
    cursor: pointer;
    transition: border-color 0.12s, color 0.12s;
    align-self: flex-start;

    &:hover { border-color: #487BFD; color: #487BFD; }
`;

const ApplyButton = styled.button`
    align-self: flex-end;
    padding: 7px 16px;
    border: none;
    border-radius: 7px;
    background: #487BFD;
    font-family: Pretendard;
    font-size: 13px;
    font-weight: 600;
    color: #ffffff;
    cursor: pointer;
    transition: opacity 0.12s;

    &:hover:not(:disabled) { opacity: 0.85; }
    &:disabled { opacity: 0.35; cursor: not-allowed; }
`;

const EmptySlotText = styled.span`
    font-family: Pretendard;
    font-size: 13px;
    font-weight: 400;
    color: #cccccc;
    padding-top: 2px;
`;

const OverlapWarning = styled.span`
    font-family: Pretendard;
    font-size: 12px;
    font-weight: 500;
    color: #ff4444;
`;

export default ChannelDetailPage;
